// views/Dashboard/PainelExecutivo/sections/ceo/FaturamentoPorUsina.tsx
//
// "Meta de faturamento mensal para cada usina — e qual o realizado."
//
// O mês aqui é o de REFERÊNCIA das contas, o mesmo do bloco de energia (os dois
// andam juntos), e abre no último mês já faturado: as contas de outubro chegam
// ao longo de novembro, e abrir no mês corrente pintaria toda usina de vermelho
// só por atraso de conta.
import React, { useMemo, useState } from 'react';
import { Loader2, PencilLine } from 'lucide-react';
import { useFinanceiroDashboardData } from '../financeiro/useFinanceiroDashboardData';
import type { useMetasFaturamento } from './useCeoDados';
import {
  faixaDoAtingimento, financeiroPorUsina, isoDoMes, mesDoOrdinal, metaVigente, ordinalDoMes,
} from '../../../../../src/lib/dashboard/painelCeo';
import type { UsinaCeo } from '../../../../../src/lib/dashboard/painelCeo';
import { parteVigorDaFatura } from '../../../../../src/lib/financeiro/parteVigor';
import { useToast } from '../../../../../src/contexts/ToastContext';
import {
  BarraMeta, Bloco, BotaoPrimario, BotaoSecundario, COR_FAIXA, Destaque, ErroBloco, LinhasCarregando, PassoMes,
  brl, brlCurto, pct,
} from './ui';

const GRADE = 'md:grid md:grid-cols-[minmax(150px,1fr)_minmax(260px,2.4fr)_minmax(150px,auto)] md:items-center md:gap-6';

/** "120.000", "120000" e "120.000,50" viram número. Vazio = sem meta. */
const lerReais = (texto: string): number | null => {
  const limpo = texto.replace(/[^\d,]/g, '').replace(',', '.');
  if (!limpo) return null;
  const v = Number(limpo);
  return Number.isFinite(v) ? v : null;
};
const escreverReais = (v: number | null) => (v == null ? '' : v.toLocaleString('pt-BR', { maximumFractionDigits: 2 }));

interface Props {
  usinas: UsinaCeo[];
  carregandoUsinas: boolean;
  metas: ReturnType<typeof useMetasFaturamento>;
  mes: string;
  onMes: (mes: string) => void;
  /** Último mês com as contas todas chegadas. */
  fechado: string;
  ordMinimo: number;
  ordAtual: number;
}

const FaturamentoPorUsina: React.FC<Props> = ({ usinas, carregandoUsinas, metas, mes, onMes, fechado, ordMinimo, ordAtual }) => {
  const { dados, percentualVigorPorParceira, loading, erro } = useFinanceiroDashboardData();
  const { addToast } = useToast();

  const ordMes = ordinalDoMes(mes) ?? ordAtual - 1;
  const emAndamento = ordMes > (ordinalDoMes(fechado) ?? ordAtual - 1);
  const mesIso = isoDoMes(mes) ?? '';

  const linhas = useMemo(
    () => financeiroPorUsina(dados, usinas, mes, (f) => parteVigorDaFatura(f, percentualVigorPorParceira)),
    [dados, usinas, mes, percentualVigorPorParceira],
  );
  const metaDe = (usinaId: string) => metaVigente(metas.metas, usinaId, mesIso);

  const totalFaturado = linhas.reduce((s, l) => s + l.faturado, 0);
  const comMeta = usinas.filter((u) => metaDe(u.id) != null);
  const metaTotal = comMeta.reduce((s, u) => s + (metaDe(u.id) ?? 0), 0);
  const faturadoComMeta = linhas.filter((l) => l.usinaId && comMeta.some((u) => u.id === l.usinaId)).reduce((s, l) => s + l.faturado, 0);
  const atingimentoTotal = metaTotal > 0 ? faturadoComMeta / metaTotal : null;
  // Sem meta, a barra compara as usinas entre si (a maior = cheia): o espaço não fica vazio.
  const maiorFaturado = Math.max(1, ...linhas.slice(0, usinas.length).map((l) => l.faturado));

  const contasDoMes = useMemo(() => dados.filter((f) => f.mes_referencia === mes).length, [dados, mes]);
  const contasDoFechado = useMemo(() => dados.filter((f) => f.mes_referencia === fechado).length, [dados, fechado]);

  // ───── edição das metas (no lugar, sem modal) ─────
  const [rascunho, setRascunho] = useState<Record<string, string> | null>(null);
  const [salvando, setSalvando] = useState(false);
  const editando = rascunho !== null;

  const abrirEdicao = () => setRascunho(Object.fromEntries(usinas.map((u) => [u.id, escreverReais(metaDe(u.id))])));
  const salvarMetas = async () => {
    if (!rascunho) return;
    const mudou = usinas
      .map((u) => ({ usinaId: u.id, meta: lerReais(rascunho[u.id] ?? '') ?? 0 }))
      .filter((v) => (metaDe(v.usinaId) ?? 0) !== v.meta);
    setSalvando(true);
    try {
      await metas.salvar(mesIso, mudou);
      addToast(mudou.length ? `Metas salvas. Valem de ${mes} em diante.` : 'Nenhuma meta mudou.', 'success');
      setRascunho(null);
    } catch (e: any) {
      addToast(e?.message || 'Não foi possível salvar as metas.', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const carregando = (loading && dados.length === 0) || carregandoUsinas || metas.loading;
  const erroBloco = erro || metas.erro;
  const outras = linhas[linhas.length - 1];

  const titulo = carregando || erroBloco
    ? 'Faturamento por usina'
    : atingimentoTotal != null
      ? `Faturamento de ${mes} em ${pct(atingimentoTotal)} da meta`
      : `${brlCurto(totalFaturado)} faturados em ${mes}`;
  // Os piores primeiro, e só três nomes: a lista inteira numa frase não se lê.
  const piores = usinas
    .map((u, i) => ({ u, a: metaDe(u.id) ? linhas[i].faturado / metaDe(u.id)! : null }))
    .filter((x): x is { u: UsinaCeo; a: number } => x.a != null && x.a < 0.95)
    .sort((x, y) => x.a - y.a);
  const subtitulo = atingimentoTotal != null
    ? piores.length
      ? `${piores.length} de ${comMeta.length} usinas abaixo da meta · mais longe: ${piores.slice(0, 3).map((x) => `${x.u.nome} (${pct(x.a)})`).join(', ')}`
      : 'Todas as usinas com meta bateram a meta.'
    : 'Parte da Vigor nas contas do mês. Defina a meta de cada usina para comparar.';

  return (
    <Bloco
      titulo={titulo}
      subtitulo={subtitulo}
      acoes={
        <>
          <PassoMes
            mes={mes}
            desligado={editando}
            podeVoltar={ordMes > ordMinimo}
            podeAvancar={ordMes < ordAtual}
            onVoltar={() => onMes(mesDoOrdinal(ordMes - 1))}
            onAvancar={() => onMes(mesDoOrdinal(ordMes + 1))}
          />
          {editando ? (
            <>
              <BotaoPrimario onClick={salvarMetas} disabled={salvando}>
                {salvando && <Loader2 size={13} className="animate-spin" />} Salvar metas
              </BotaoPrimario>
              <BotaoSecundario onClick={() => setRascunho(null)} disabled={salvando}>Cancelar</BotaoSecundario>
            </>
          ) : (
            <BotaoSecundario onClick={abrirEdicao} disabled={carregando || usinas.length === 0}>
              <PencilLine size={13} /> {comMeta.length ? 'Editar metas' : 'Definir metas'}
            </BotaoSecundario>
          )}
        </>
      }
      destaque={
        carregando || erroBloco ? null : atingimentoTotal != null ? (
          <Destaque
            valor={pct(atingimentoTotal)}
            tom={COR_FAIXA[faixaDoAtingimento(atingimentoTotal)].texto}
            legenda={`${brlCurto(faturadoComMeta)} de ${brlCurto(metaTotal)}`}
            title={`${brl(faturadoComMeta)} de ${brl(metaTotal)}`}
          />
        ) : (
          <Destaque valor={brlCurto(totalFaturado)} legenda={`faturado em ${mes}`} title={brl(totalFaturado)} />
        )
      }
    >
      {erroBloco ? (
        <ErroBloco mensagem={erroBloco} />
      ) : carregando ? (
        <LinhasCarregando linhas={6} rotulo="Separando as contas por usina…" />
      ) : (
        <>
          {editando ? (
            <p className="mx-5 mb-3 rounded-lg bg-primary/5 px-3 py-2 text-xs text-primary-dark dark:bg-primary-light/10 dark:text-primary-light">
              Meta mensal em reais, parte da Vigor. Vale de <strong>{mes}</strong> em diante; os meses anteriores continuam com a meta que tinham. Deixe vazio para tirar a meta.
            </p>
          ) : emAndamento ? (
            <p className="mx-5 mb-3 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:bg-sky-900/25 dark:text-sky-300">
              {mes} ainda está chegando: {contasDoMes.toLocaleString('pt-BR')} contas até agora, contra {contasDoFechado.toLocaleString('pt-BR')} em {fechado}. O número vai subir até o fim do mês que vem.
            </p>
          ) : !comMeta.length ? (
            <p className="mx-5 mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              Nenhuma usina tem meta de faturamento ainda. Use <strong>Definir metas</strong> para colocar o valor mensal de cada uma.
            </p>
          ) : null}

          <div className={`hidden border-y border-gray-100 bg-gray-50/70 px-5 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-400 ${GRADE}`}>
            <span>Usina</span>
            <span>{editando ? `Meta mensal (a partir de ${mes})` : 'Faturado x meta'}</span>
            <span className="text-right">Faturado / meta</span>
          </div>

          <ul className="divide-y divide-gray-100 dark:divide-gray-700/70">
            {usinas.map((u, i) => {
              const l = linhas[i];
              const meta = metaDe(u.id);
              const atingimento = meta ? l.faturado / meta : null;
              const faixa = faixaDoAtingimento(atingimento);
              return (
                <li key={u.id} className={`px-5 py-3 transition-colors duration-150 hover:bg-gray-50/80 dark:hover:bg-gray-900/30 ${GRADE}`}>
                  <div className="flex items-baseline justify-between gap-3 md:block">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{u.nome}</p>
                    <p className="text-[11px] tabular-nums text-gray-400">{l.boletos.toLocaleString('pt-BR')} boletos</p>
                  </div>

                  {editando ? (
                    <label className="mt-2 flex items-center gap-2 md:mt-0">
                      <span className="text-xs font-semibold text-gray-500">R$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={rascunho?.[u.id] ?? ''}
                        onChange={(e) => setRascunho((r) => ({ ...(r ?? {}), [u.id]: e.target.value }))}
                        onBlur={(e) => setRascunho((r) => ({ ...(r ?? {}), [u.id]: escreverReais(lerReais(e.target.value)) }))}
                        placeholder="sem meta"
                        aria-label={`Meta mensal de ${u.nome}`}
                        className="w-full max-w-xs rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm tabular-nums text-gray-900 outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                      />
                    </label>
                  ) : meta ? (
                    <div className="mt-2 flex items-center gap-3 md:mt-0">
                      <span className={`w-12 shrink-0 text-sm font-bold tabular-nums ${COR_FAIXA[faixa].texto}`}>{pct(atingimento)}</span>
                      <BarraMeta fracao={atingimento} faixa={faixa} ordem={i} className="flex-1" />
                    </div>
                  ) : (
                    <div className="mt-2 flex items-center gap-3 md:mt-0" title="Sem meta: a barra compara com a usina que mais faturou">
                      <span className="w-12 shrink-0 text-[11px] text-gray-400">sem meta</span>
                      <div className="h-2 flex-1 rounded-full bg-gray-100 dark:bg-gray-700/70">
                        <div className="h-full rounded-full bg-gray-300 dark:bg-gray-500" style={{ width: `${(l.faturado / maiorFaturado) * 100}%` }} />
                      </div>
                    </div>
                  )}

                  <p className="mt-1 text-sm tabular-nums text-gray-900 md:mt-0 md:text-right dark:text-white" title={`${brl(l.faturado)}${meta ? ` de ${brl(meta)}` : ''}`}>
                    <strong className="font-semibold">{brlCurto(l.faturado)}</strong>
                    {meta ? <span className="text-gray-400"> de {brlCurto(meta)}</span> : null}
                  </p>
                </li>
              );
            })}

            {/* O que não sai de usina da Vigor fecha a conta do mês. */}
            {outras && outras.faturado > 0 && (
              <li className={`px-5 py-3 text-gray-500 dark:text-gray-400 ${GRADE}`}>
                <div>
                  <p className="text-sm font-medium">Outras origens</p>
                  <p className="text-[11px] text-gray-400">contas sem usina da Vigor: parceiras, avulsas, terceiros</p>
                </div>
                <span className="hidden md:block" />
                <p className="mt-1 text-sm tabular-nums md:mt-0 md:text-right" title={brl(outras.faturado)}>{brlCurto(outras.faturado)}</p>
              </li>
            )}
          </ul>

          <footer className="flex flex-wrap items-baseline justify-between gap-2 border-t border-gray-100 px-5 py-3 text-xs dark:border-gray-700">
            <span className="max-w-3xl text-gray-500 dark:text-gray-400">
              Total faturado em {mes} (parte da Vigor).{comMeta.length > 0 && <> As metas iniciais (19/10/2026) são o que cada usina deve gerar no mês, pelo laudo, × R$ 0,70 por kWh — o preço médio que os clientes pagaram de julho a setembro. Fica abaixo da meta a usina com energia sobrando ou com muitos clientes de parceira (a Vigor fica só com a parte dela).</>}
            </span>
            <span className="font-semibold tabular-nums text-gray-900 dark:text-white" title={brl(totalFaturado)}>{brlCurto(totalFaturado)}</span>
          </footer>
        </>
      )}
    </Bloco>
  );
};

export default FaturamentoPorUsina;
